import { Trans, t } from '@lingui/macro';

import { LoadingButton, DesktopDatePicker } from '@mui/lab';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  TableBody,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { useSearch } from 'hooks';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { PopupController } from 'types/Common';
import { TransReviseConfirmType, TransReviseType } from 'types/Transaction';
import { formatNumber } from 'utils/common';
import { transactionService, queryClient } from 'services';
import { DateTime } from 'luxon';

export const useStyles = makeStyles({
  table: {
    minWidth: 650,
    '& .MuiTableCell-root': {
      border: '1px solid rgba(224, 224, 224, 1)',
    },
  },
  cellTitle: {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    width: '30%',
  },
  cellValue: {
    width: '20%',
  },
});

type PopupProps = PopupController & {
  transactionId: number;
  salaryPaymentDate?: string;
  onCreated: (item: TransReviseType) => void;
};

const TransRevisePopup = ({ transactionId, salaryPaymentDate, onCreated, onClose }: PopupProps) => {
  const [dataSearch, onSearchRevise] = useSearch({
    transId: transactionId,
    salaryPaymentDate: `${salaryPaymentDate}`,
  });

  const { control, handleSubmit } = useForm();
  const classes = useStyles();

  const { data: transaction } = useQuery(
    ['transactionService.calculateTransactionRevise', dataSearch],
    () => transactionService.calculateTransactionRevise(dataSearch),
    { keepPreviousData: true },
  );

  const { mutate: addTransactionRevise, isLoading } = useMutation(transactionService.addTransactionRevise, {
    onSuccess: (newTransRevise: TransReviseType) => {
      queryClient.invalidateQueries('transactionService.fetchTransactionRevises');
      onCreated(newTransRevise);
      onClose();
    },
  });

  const handleClickSubmit = () => {
    handleSubmit((values: TransReviseConfirmType) => {
      type ValuesType = TransReviseConfirmType & { salaryPaymentDate: DateTime };
      const { salaryPaymentDate, ...others } = values as ValuesType;
      addTransactionRevise({
        ...others,
        salaryPaymentDate: salaryPaymentDate.toISO(),
        transId: transactionId,
      });
    })();
  };

  return (
    <>
      <DialogTitle>
        <Trans>Transaction Revision</Trans>
      </DialogTitle>

      <DialogContent>
        <TableContainer component={Paper}>
          <Table className={classes.table}>
            <TableBody>
              <TableRow>
                <TableCell className={classes.cellTitle}>
                  <Trans>Expected Salary Payment Date</Trans>
                </TableCell>
                <TableCell className={classes.cellValue}>
                  {salaryPaymentDate ? DateTime.fromISO(salaryPaymentDate).toFormat('dd/MM/yyyy') : ''}
                </TableCell>
                <TableCell className={classes.cellTitle}>
                  <Trans>Revised Salary Payment Date</Trans>
                </TableCell>
                <TableCell className={classes.cellValue}>
                  <Controller
                    name='salaryPaymentDate'
                    defaultValue={
                      salaryPaymentDate
                        ? DateTime.fromISO(salaryPaymentDate).toFormat('dd/MM/yyyy')
                        : transaction?.salaryPaymentDate
                        ? DateTime.fromISO(transaction?.salaryPaymentDate).toFormat('dd/MM/yyyy')
                        : ''
                    }
                    control={control}
                    rules={{
                      validate: { isValid: (value: DateTime) => value && value.isValid },
                      required: t`Field is required`,
                    }}
                    render={({ field: { value, onChange: setValue }, fieldState: { invalid, error } }) => (
                      <DesktopDatePicker
                        value={value}
                        onChange={(value) => {
                          setValue(value);
                          onSearchRevise({
                            transId: transactionId,
                            salaryPaymentDate: value.toISO(),
                          });
                        }}
                        renderInput={(props) => <TextField {...props} required error={invalid} />}
                        inputFormat='dd/MM/yyyy'
                      />
                    )}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.cellTitle}>
                  <Trans>Received Amount (VND)</Trans>
                </TableCell>
                <TableCell className={classes.cellValue}>{formatNumber(transaction?.collectionAmount)}</TableCell>
                <TableCell className={classes.cellTitle}>
                  <Trans>Revised Term (Day)</Trans>
                </TableCell>
                <TableCell className={classes.cellValue}>{formatNumber(transaction?.appliedTerm)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.cellTitle}>
                  <Trans>Earned Discount Amount (VND)</Trans>
                </TableCell>
                <TableCell className={classes.cellValue}>{formatNumber(transaction?.earnedDiscountAmount)}</TableCell>
                <TableCell className={classes.cellTitle}>
                  <Trans>Future Discount Amount (VND)</Trans>
                </TableCell>
                <TableCell className={classes.cellValue}>{formatNumber(transaction?.futureDiscountAmount)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.cellTitle}>
                  <Trans>Promotion of Transaction Cost (VND)</Trans>
                </TableCell>
                <TableCell className={classes.cellValue}>{formatNumber(transaction?.promotionAmount)}</TableCell>
                <TableCell className={classes.cellTitle}>
                  <Trans>Total Revised Transaction Cost (VND)</Trans>
                </TableCell>
                <TableCell className={classes.cellValue}>{formatNumber(transaction?.costAmount)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.cellTitle}>
                  <Trans>Revised Debt Purchasing Price (VND)</Trans>
                </TableCell>
                <TableCell className={classes.cellValue}>
                  {formatNumber(transaction?.debtPurchasingPriceAmount)}
                </TableCell>
                <TableCell className={classes.cellTitle}>
                  <Trans>Bank Fee after tax (VND)</Trans>
                </TableCell>
                <TableCell className={classes.cellValue}>{formatNumber(transaction?.bankTransactionFee)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} className={classes.cellTitle}>
                  <b>
                    <Trans>Collection Amount from Employee (VND)</Trans>
                  </b>
                </TableCell>
                <TableCell colSpan={2} className={classes.cellValue}>
                  {formatNumber(transaction?.collectionAmount)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant='outlined' onClick={onClose}>
          <Trans>Cancel</Trans>
        </LoadingButton>
        <LoadingButton variant='contained' onClick={handleClickSubmit} loading={isLoading}>
          <Trans>Confirm</Trans>
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default TransRevisePopup;
